import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, map, of, tap } from 'rxjs';
import { Book } from '../models/book.model';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

const STORAGE_KEY = 'at_wishlist_book_ids';

@Injectable({ providedIn: 'root' })
export class WishlistService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly ids = signal<number[]>(this.readIds());

  readonly bookIds = this.ids.asReadonly();
  readonly count = computed(() => this.ids().length);

  has(bookId: number): boolean {
    return this.ids().includes(bookId);
  }

  /** Load wishlist from API when signed in; keep local for guests. */
  load(): void {
    const user = this.auth.currentUser();
    if (!user?.UsrId) return;
    this.api
      .get<Array<{ WishBookId?: number; wishBookId?: number }>>(`/Wishlist/${user.UsrId}`)
      .pipe(
        map((rows) =>
          (Array.isArray(rows) ? rows : [])
            .map((r) => Number(r.WishBookId ?? r.wishBookId))
            .filter((n) => Number.isFinite(n) && n > 0)
        ),
        catchError(() => of(this.readIds())),
        tap((list) => {
          this.ids.set(list);
          this.persist(list);
        })
      )
      .subscribe();
  }

  toggle(book: Book): boolean {
    const id = Number(book?.BookId);
    if (!Number.isFinite(id) || id <= 0) return false;

    const user = this.auth.currentUser();
    const currently = this.ids().includes(id);
    const next = currently ? this.ids().filter((x) => x !== id) : [...this.ids(), id];
    this.ids.set(next);
    this.persist(next);

    if (user?.UsrId) {
      this.api
        .post<{ Added?: boolean; added?: boolean }>('/Wishlist/toggle', {
          UserId: user.UsrId,
          BookId: id
        })
        .pipe(
          tap((res) => {
            const added = res?.Added ?? res?.added;
            if (typeof added === 'boolean') {
              const synced = added
                ? Array.from(new Set([...this.ids(), id]))
                : this.ids().filter((x) => x !== id);
              this.ids.set(synced);
              this.persist(synced);
            }
          }),
          catchError(() => of(null))
        )
        .subscribe();
    }

    return !currently;
  }

  remove(bookId: number): void {
    const next = this.ids().filter((x) => x !== bookId);
    this.ids.set(next);
    this.persist(next);
    const user = this.auth.currentUser();
    if (user?.UsrId) {
      this.api.delete(`/Wishlist/${user.UsrId}/${bookId}`).pipe(catchError(() => of(null))).subscribe();
    }
  }

  clear(): void {
    const prev = this.ids();
    this.ids.set([]);
    this.persist([]);
    const user = this.auth.currentUser();
    if (user?.UsrId) {
      for (const bookId of prev) {
        this.api.delete(`/Wishlist/${user.UsrId}/${bookId}`).pipe(catchError(() => of(null))).subscribe();
      }
    }
  }

  booksFrom(catalogue: Book[]): Book[] {
    const set = new Set(this.ids());
    return catalogue.filter((b) => set.has(b.BookId));
  }

  private readIds(): number[] {
    if (!isPlatformBrowser(this.platformId)) return [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.map(Number).filter((n) => Number.isFinite(n) && n > 0);
    } catch {
      return [];
    }
  }

  private persist(ids: number[]): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {
      /* ignore */
    }
  }
}
